import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { ConversationStreamError, conversationFailure, MAX_CONVERSATION_MESSAGE_LENGTH } from "@/api/agent-conversation";
import {
  INITIAL_CONVERSATION_STATE,
  conversationReducer,
  isConversationEnded,
} from "@/components/agents/conversation/conversationReducer";
import type {
  ConversationAction,
  ConversationAdapter,
  ConversationScreenState,
} from "@/components/agents/conversation/types";

/** Intervalo do polling de reserva: o mesmo do terminal do Nó 02. */
export const POLL_INTERVAL_MS = 2000;
/** Espera antes de tentar religar o stream que caiu. */
export const RECONNECT_DELAY_MS = 5000;

export type ConversationConnection = "connecting" | "live" | "polling" | "closed" | "error" | "retry";

export interface AgentConversationHandle {
  state: ConversationScreenState;
  connection: ConversationConnection;
  connectionError: string | null;
  retryConnection: (() => void) | null;
  send: (text: string, replyTo?: string | null) => Promise<void>;
  retry: (clientMessageId: string) => void;
  interrupt: () => Promise<void>;
  end: () => Promise<void>;
}

interface ConversationGeneration {
  adapter: ConversationAdapter;
  active: boolean;
  state: () => ConversationScreenState;
  deliveries: Map<string, Promise<void>>;
  closingAttempts: Map<string, number>;
  failure: string | null;
  fail: (error: unknown) => void;
  retryConnection: (() => void) | null;
  apply: (action: ConversationAction) => void;
}

/**
 * Reidrata pelo log, segue pelo SSE e usa polling durante a reconexão.
 * Cada troca de adaptador começa outra geração: respostas e callbacks antigos
 * não podem alterar a conversa nova. O evento durável confirma a mensagem;
 * o POST apenas informa o encaminhamento, inclusive quando o ID é reenviado.
 */
export function useAgentConversation(adapter: ConversationAdapter): AgentConversationHandle {
  const [state, dispatch] = useReducer(conversationReducer, INITIAL_CONVERSATION_STATE);
  const [connection, setConnection] = useState<ConversationConnection>("connecting");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const generationRef = useRef<ConversationGeneration | null>(null);

  useEffect(() => {
    // A projeção síncrona dá o seq e o primeiro status terminal antes do repaint.
    let currentState = INITIAL_CONVERSATION_STATE;
    let stream: AbortController | null = null;
    let streamGeneration = 0;
    let viewGeneration = 0;
    let appliedViewGeneration = 0;
    let connectionRetries = 0;
    let suspended = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const generation: ConversationGeneration = {
      adapter,
      active: true,
      state: () => currentState,
      deliveries: new Map(),
      closingAttempts: new Map(),
      failure: null,
      fail: () => {},
      retryConnection: null,
      apply: (action) => {
        if (!generation.active) return;
        try {
          currentState = conversationReducer(currentState, action);
          dispatch(action);
        } catch (error) {
          generation.fail(new ConversationStreamError("stream_callback_error", error));
        }
      },
    };
    generationRef.current = generation;
    generation.apply({ type: "reset" });
    setConnection("connecting");
    setConnectionError(null);
    const ended = () => isConversationEnded(currentState.status);

    const stopPolling = () => {
      if (pollTimer !== null) clearInterval(pollTimer);
      pollTimer = null;
    };
    const abortStream = () => {
      // Invalida antes do abort, que pode disparar onClose de forma síncrona.
      streamGeneration += 1;
      const previous = stream;
      stream = null;
      previous?.abort();
    };
    const close = () => {
      if (!generation.active) return;
      stopPolling();
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      abortStream();
      setConnection("closed");
    };

    generation.fail = (error) => {
      if (!generation.active) return;
      const failure = conversationFailure(error);
      if (failure.retryable && !failure.closing) return;
      close();
      suspended = true;
      setConnectionError(failure.code);
      if (failure.closing) {
        setConnection("retry");
        generation.retryConnection = connectionRetries < 1 ? () => {
          if (!generation.active || !suspended || connectionRetries >= 1) return;
          connectionRetries += 1;
          generation.retryConnection = null;
          suspended = false;
          setConnectionError(null);
          setConnection("connecting");
          void loadView().then((loaded) => {
            if (loaded) openStream();
            else fallBack();
          });
        } : null;
      } else {
        generation.failure = failure.code;
        generation.retryConnection = null;
        setConnection("error");
      }
    };

    const loadView = async (valid = () => true): Promise<boolean> => {
      if (!generation.active || suspended) return false;
      const requestId = ++viewGeneration;
      const afterSeq = currentState.lastSeq;
      let view;
      try {
        view = await adapter.fetchView(afterSeq);
      } catch (error) {
        if (!suspended && valid() && requestId >= appliedViewGeneration) generation.fail(error);
        // A falha de transporte entra no polling; o próximo tick tenta de novo.
        return false;
      }
      if (!generation.active || suspended || !valid() || requestId < appliedViewGeneration || view.run_id !== adapter.runId) return false;
      try {
        const events = view.events.filter((event) => event.run_id === adapter.runId);
        // O dono só vem com `after_seq=0`: se esta leitura for descartada como velha, o
        // polling (`after_seq>0`) não o traz de novo. Ele não envelhece, então grava antes.
        if (view.requested_by) {
          generation.apply({
            type: "executor_loaded",
            runId: view.run_id,
            requestedBy: view.requested_by,
            requestedByName: view.requested_by_name ?? null,
          });
        }
        if (Math.max(afterSeq, ...events.map((event) => event.seq)) < currentState.lastSeq) return false;
        // Carga em voo pode trazer evento durável após snapshot terminal.
        appliedViewGeneration = requestId;
        generation.apply({ type: "view_loaded", view: { ...view, events } });
        if (ended()) close();
        return !suspended;
      } catch (error) {
        generation.fail(new ConversationStreamError("stream_callback_error", error));
        return false;
      }
    };

    const fallBack = () => {
      if (!generation.active || suspended) return;
      if (ended()) return close();
      abortStream();
      setConnection("polling");
      if (pollTimer === null) {
        pollTimer = setInterval(() => {
          void loadView();
        }, POLL_INTERVAL_MS);
      }
      if (reconnectTimer === null) reconnectTimer = setTimeout(openStream, RECONNECT_DELAY_MS);
    };

    function openStream() {
      reconnectTimer = null;
      if (!generation.active || suspended) return;
      if (ended()) return close();
      const streamId = ++streamGeneration;
      const isCurrent = () => generation.active && streamId === streamGeneration && !ended() && !suspended;
      try {
        const opened = adapter.openStream(currentState.lastSeq, {
          onOpen: () => {
            if (!isCurrent()) return;
            if (streamId === 1) {
              setConnection("live");
              return;
            }
            stopPolling();
            void loadView(isCurrent).then((loaded) => {
              if (!isCurrent()) return;
              if (!loaded) return fallBack();
              stopPolling();
              setConnectionError(null);
              setConnection("live");
            });
          },
          onEvent: (event) => {
            if (!isCurrent() || event.run_id !== adapter.runId) return;
            generation.apply({ type: "events_received", events: [event] });
            if (ended()) close();
          },
          onAgentStatus: (activity) => {
            if (isCurrent()) generation.apply({ type: "agent_status_received", activity });
          },
          onClose: () => {
            if (isCurrent()) fallBack();
          },
          onError: (error) => {
            if (!isCurrent()) return;
            generation.fail(error);
            if (!suspended) fallBack();
          },
        });
        if (isCurrent()) stream = opened;
        else opened.abort();
      } catch (error) {
        if (isCurrent()) {
          generation.fail(new ConversationStreamError("stream_callback_error", error));
        }
      }
    }

    void loadView().then((loaded) => {
      if (!generation.active) return;
      if (loaded) openStream();
      else fallBack();
    });

    return () => {
      generation.active = false;
      abortStream();
      stopPolling();
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    };
  }, [adapter]);

  const deliver = useCallback(
    (clientMessageId: string, text: string, replyTo: string | null): Promise<void> => {
      const generation = generationRef.current;
      if (!generation?.active || generation.adapter !== adapter) return Promise.resolve();
      const existing = generation.deliveries.get(clientMessageId);
      if (existing) return existing;
      const request = (async () => {
        try {
          const routing = await adapter.sendMessage({
            client_message_id: clientMessageId,
            text,
            reply_to: replyTo,
          });
          generation.apply({ type: "message_routed", clientMessageId, outcome: routing.outcome });
        } catch (error) {
          const failure = conversationFailure(error);
          const attempts = (generation.closingAttempts.get(clientMessageId) ?? 0) + (failure.closing ? 1 : 0);
          generation.closingAttempts.set(clientMessageId, attempts);
          generation.apply({ type: "message_failed", clientMessageId, errorCode: failure.code,
            retryable: failure.retryable && (!failure.closing || attempts < 2) });
          if (!failure.retryable) generation.fail(error);
          throw error;
        } finally {
          generation.deliveries.delete(clientMessageId);
        }
      })();
      generation.deliveries.set(clientMessageId, request);
      return request;
    },
    [adapter],
  );

  const send = useCallback(
    async (text: string, replyTo: string | null = null): Promise<void> => {
      const generation = generationRef.current;
      if (!generation?.active || generation.adapter !== adapter) return;
      const pending = replyTo === null ? undefined : generation.state().pending.find(
        (message) => message.replyTo === replyTo && message.text === text,
      );
      if (generation.failure) throw { detail: generation.failure };
      if (pending?.state === "failed" && !pending.retryable) throw { detail: pending.errorCode };
      const clientMessageId = pending?.clientMessageId ?? crypto.randomUUID();
      generation.apply({ type: "message_sending", clientMessageId, text, replyTo });
      if (text.length > MAX_CONVERSATION_MESSAGE_LENGTH) {
        generation.apply({ type: "message_failed", clientMessageId, errorCode: "message_too_long", retryable: false });
        throw { status_code: 422, detail: "message_too_long" };
      }
      await deliver(clientMessageId, text, replyTo);
    },
    [adapter, deliver],
  );
  const retry = useCallback(
    (clientMessageId: string) => {
      const generation = generationRef.current;
      if (!generation?.active || generation.adapter !== adapter) return;
      const message = generation.state().pending.find((candidate) => candidate.clientMessageId === clientMessageId);
      if (!message || (!message.retryable && !generation.deliveries.has(clientMessageId)) || generation.failure) return;
      generation.apply({
        type: "message_sending",
        clientMessageId,
        text: message.text,
        replyTo: message.replyTo,
      });
      void deliver(clientMessageId, message.text, message.replyTo).catch(() => {});
    },
    [adapter, deliver],
  );

  const interrupt = useCallback(() => adapter.interrupt(), [adapter]);
  const end = useCallback(() => adapter.end(), [adapter]);

  return { state, connection, connectionError, retryConnection: generationRef.current?.retryConnection ?? null, send, retry, interrupt, end };
}
